import { defineStore } from 'pinia';
import {
  clearPersistedSessionState,
  readPersistedSessionState,
  writePersistedSessionState,
} from '@/stores/persistence/pinia-session-storage';
import {
  scoutSessionStorageKey,
  type PersistedScoutSidebarOpenState,
  type ScoutSessionScope,
} from '@/stores/scout-sidebar-session';
import { LEFT_SIDEBAR, RIGHT_SIDEBAR } from '@/types/ui';

type DefaultOpenSidebar = 'left' | 'right';

export const useSidebarStore = defineStore('sidebars', {
  state: () => ({
    sidebars: {} as Record<string, boolean>,
  }),
  actions: {
    open(id: string) {
      this.sidebars[id] = true;
    },
    close(id: string) {
      this.sidebars[id] = false;
    },
    toggle(id: string) {
      this.sidebars[id] = !this.isOpen(id);
    },
    isOpen(id: string) {
      return this.sidebars[id] ?? false;
    },
    register(id: string, defaultValue = false) {
      if (!(id in this.sidebars)) {
        this.sidebars[id] = defaultValue;
      }
    },
    unregister(id: string) {
      delete this.sidebars[id];
    },
    applyPageDefaultSidebars(
      defaultOpenSidebar: DefaultOpenSidebar,
      hasAi: boolean,
    ) {
      const openRightByDefault = defaultOpenSidebar === 'right' && hasAi;
      this.sidebars[RIGHT_SIDEBAR] = openRightByDefault;
      this.sidebars[LEFT_SIDEBAR] = !openRightByDefault;
    },
    hydrateScoutOpenFromSession(scope: ScoutSessionScope): boolean {
      const key = scoutSessionStorageKey(scope);
      if (!key) {
        return false;
      }

      const payload = readPersistedSessionState<PersistedScoutSidebarOpenState>(
        key,
      );
      if (payload == null || typeof payload.isOpen !== 'boolean') {
        return false;
      }

      this.sidebars[RIGHT_SIDEBAR] = payload.isOpen;
      this.sidebars[LEFT_SIDEBAR] = !payload.isOpen;
      return true;
    },
    persistScoutOpenToSession(scope: ScoutSessionScope) {
      const key = scoutSessionStorageKey(scope);
      if (!key) {
        return;
      }

      writePersistedSessionState(key, {
        isOpen: this.isOpen(RIGHT_SIDEBAR),
      } satisfies PersistedScoutSidebarOpenState);
    },
    clearScoutOpenSession(scope: ScoutSessionScope) {
      const key = scoutSessionStorageKey(scope);
      if (!key) {
        return;
      }

      clearPersistedSessionState(key);
    },
  },
});
